import { memo, useState, useRef, useCallback, useEffect } from "react";
import { Video, X, Send, RotateCcw, Camera as CameraIcon } from "lucide-react";
import { useAuthContext } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface VideoMessageRecorderProps {
  chatId: string;
  onVideoSent: () => void;
  onCancel: () => void;
  isFirstMessage?: boolean;
}

const VideoMessageRecorder = memo(({
  chatId,
  onVideoSent,
  onCancel,
  isFirstMessage = false,
}: VideoMessageRecorderProps) => {
  const { user } = useAuthContext();
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");

  const maxDuration = 10; // 10 seconds max for first message
  const minDuration = 5; // 5 seconds min

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 720 }, height: { ideal: 1280 } },
        audio: true,
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      toast({
        title: "Camera access denied",
        description: "Please allow camera access to record a video message",
        variant: "destructive",
      });
    }
  }, [facingMode]);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  }, [stream]);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  // Flip camera
  const flipCamera = useCallback(() => {
    stopCamera();
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
    setTimeout(startCamera, 100);
  }, [stopCamera, startCamera]);

  // Start recording
  const startRecording = useCallback(() => {
    if (!stream) return;

    chunksRef.current = [];
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : "video/webm",
    });

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(100);
    setIsRecording(true);
    setRecordingTime(0);
  }, [stream]);

  // Recording timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= maxDuration) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      stopCamera();
    }
  }, [isRecording, stopCamera]);

  // Re-record
  const reRecord = useCallback(() => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
    startCamera();
  }, [recordedUrl, startCamera]);

  // Send video message
  const sendVideoMessage = useCallback(async () => {
    if (!user || !recordedBlob) return;

    if (recordingTime < minDuration) {
      toast({
        title: "Video too short",
        description: `Record at least ${minDuration} seconds`,
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);

    try {
      // Upload to storage
      const fileName = `${chatId}/${user.id}/${Date.now()}.webm`;
      const { error: uploadError } = await supabase.storage
        .from("chat-videos")
        .upload(fileName, recordedBlob, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("chat-videos")
        .getPublicUrl(fileName);

      // Insert message
      const { error: msgError } = await supabase.from("messages").insert({
        chat_id: chatId,
        sender_id: user.id,
        content: "Video message",
        video_url: publicUrl,
      });

      if (msgError) throw msgError;

      toast({ title: "Video sent! 🎬" });
      onVideoSent();
    } catch (error: any) {
      toast({
        title: "Failed to send",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  }, [user, recordedBlob, recordingTime, chatId, onVideoSent]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
    };
  }, [recordedUrl]);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col animate-scale-in">
      {/* Header */}
      <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between">
        <button
          onClick={onCancel}
          className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
        >
          <X className="w-6 h-6" />
        </button>
        
        {!recordedUrl && stream && (
          <button
            onClick={flipCamera}
            className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Video display */}
      <div className="flex-1 relative">
        {recordedUrl ? (
          <video
            src={recordedUrl}
            className="w-full h-full object-cover"
            controls
            autoPlay
            loop
            playsInline
          />
        ) : (
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            autoPlay
            playsInline
            muted
            style={{ transform: facingMode === "user" ? "scaleX(-1)" : "none" }}
          />
        )}

        {/* Recording indicator */}
        {isRecording && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/80 text-white">
            <span className="w-3 h-3 rounded-full bg-white animate-pulse" />
            <span className="font-medium">{recordingTime}s / {maxDuration}s</span>
          </div>
        )}

        {/* First message hint */}
        {isFirstMessage && !isRecording && !recordedUrl && (
          <div className="absolute top-16 left-4 right-4 text-center">
            <p className="text-white/90 text-sm bg-black/40 rounded-xl px-4 py-2 inline-block">
              Say hi with a quick video! 👋
            </p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center gap-6">
        {recordedUrl ? (
          <>
            <button
              onClick={reRecord}
              className="px-6 py-3 rounded-full bg-white/20 text-white font-medium hover:bg-white/30"
            >
              Re-record
            </button>
            <button
              onClick={sendVideoMessage}
              disabled={isSending}
              className="px-8 py-3 rounded-full pop-gradient text-white font-semibold flex items-center gap-2 hover:scale-105 transition-transform disabled:opacity-50"
            >
              {isSending ? (
                <>
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send
                </>
              )}
            </button>
          </>
        ) : (
          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
              isRecording
                ? "bg-red-500 scale-110"
                : "bg-white/20 border-4 border-white hover:bg-white/30"
            }`}
          >
            {isRecording ? (
              <div className="w-8 h-8 rounded-sm bg-white" />
            ) : (
              <Video className="w-8 h-8 text-white" />
            )}
          </button>
        )}
      </div>

      {/* Duration hint */}
      {!recordedUrl && !isRecording && (
        <p className="absolute bottom-4 left-0 right-0 text-center text-white/60 text-xs">
          {minDuration}-{maxDuration} seconds
        </p>
      )}
    </div>
  );
});

VideoMessageRecorder.displayName = "VideoMessageRecorder";

export default VideoMessageRecorder;
